// Firm Directory — per-firm vendor/contact management (Phase 5-δ.2).
// Three tabs: Medical Providers, Insurance Carriers, Subrogation
// Contacts. Each type has its own list + add form. Firm Admin can
// bulk-import CSVs; paralegals can add/edit individual entries.

import React, { useCallback, useEffect, useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { apiFetch } from "../api.js";

const DIRECTORY_TYPES = [
  { id: "medical_provider",     label: "Medical Providers",     singular: "Provider" },
  { id: "insurance_carrier",    label: "Insurance Carriers",    singular: "Carrier" },
  { id: "subrogation_contact",  label: "Subrogation Contacts",  singular: "Contact" },
];

const EMPTY_FORM = {
  name: "", category: "", address: "", phone: "", fax: "", email: "", notes: "",
};

export function FirmDirectoryPage({ authStatus, showNotification }) {
  const role = authStatus?.firm_user?.role || "pending";
  // Use roleAtLeast so hierarchy comparisons are correct — matches
  // the server-side require_role() rank comparison. Legacy alias
  // names (superadmin=firm_admin, attorney=associate) resolve
  // through roleRank() automatically.
  const canWrite  = roleAtLeast(role, "paralegal");
  const canImport = roleAtLeast(role, "firm_admin");
  const canDelete = roleAtLeast(role, "associate");   // attorney/associate/partner+

  const [activeType, setActiveType] = useState("medical_provider");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/firm-directory?directory_type=${activeType}`);
      setEntries(d.data || []);
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only paralegals and above can view the directory.", "warning");
      } else {
        showNotification?.(`Load failed: ${e.message}`, "error");
      }
      setEntries([]);
    }
    setLoading(false);
  }, [activeType, showNotification]);

  useEffect(() => { load(); }, [load]);

  const currentType = DIRECTORY_TYPES.find(t => t.id === activeType);

  const createEntry = async () => {
    if (!form.name.trim()) {
      showNotification?.("Name is required.", "warning");
      return;
    }
    setSaving(true);
    try {
      await apiFetch("/api/firm-directory", {
        method: "POST",
        body: JSON.stringify({ ...form, directory_type: activeType }),
      });
      setForm(EMPTY_FORM);
      setShowAdd(false);
      showNotification?.(`${currentType.singular} added.`, "success");
      await load();
    } catch (e) {
      showNotification?.(`Add failed: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const saveEdit = async (id) => {
    try {
      await apiFetch(`/api/firm-directory/${id}`, {
        method: "PATCH",
        body: JSON.stringify(editForm),
      });
      setEditingId(null);
      setEditForm(EMPTY_FORM);
      showNotification?.("Saved.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
  };

  const removeEntry = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/firm-directory/${row.id}`, {
        method: "DELETE", credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        if (res.status === 403) {
          showNotification?.("Only attorneys and above can delete entries.", "warning");
        } else {
          throw new Error(msg);
        }
        return;
      }
      showNotification?.("Deleted.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  const importCsv = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      showNotification?.("Please pick a .csv file.", "warning");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification?.("CSV too large (max 5 MB).", "warning");
      return;
    }
    setCsvBusy(true);
    setCsvResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("directory_type", activeType);
      const res = await fetch("/api/firm-directory/import-csv", {
        method: "POST", body: fd, credentials: "same-origin",
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || res.statusText);
      }
      setCsvResult(data);
      showNotification?.(
        `Import complete: ${data.inserted} new, ${data.updated} updated, ${data.skipped} skipped.`,
        "success",
      );
      await load();
    } catch (e) {
      showNotification?.(`Import failed: ${e.message}`, "error");
    }
    setCsvBusy(false);
  };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1100px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "20px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" }
      }, "Firm Directory"),
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
        "Your firm's private vendor and contact lists. Nothing is shared with other firms on the platform."
      ),
    ),

    // Type tabs
    React.createElement("div", {
      style: {
        display: "flex", gap: "4px", marginBottom: "20px",
        borderBottom: `1px solid ${C.warmGray}`,
      }
    },
      ...DIRECTORY_TYPES.map(t => {
        const active = t.id === activeType;
        return React.createElement("button", {
          key: t.id,
          onClick: () => { setActiveType(t.id); setShowAdd(false); setEditingId(null); setCsvResult(null); },
          style: {
            padding: "10px 18px",
            border: "none",
            background: "transparent",
            borderBottom: active ? `2px solid ${C.gold}` : "2px solid transparent",
            color: active ? C.navy : C.textMuted,
            fontSize: "13px",
            fontWeight: active ? 700 : 500,
            cursor: "pointer",
            marginBottom: "-1px",
          },
        }, t.label);
      }),
    ),

    // Action bar
    React.createElement("div", {
      style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", gap: "8px" }
    },
      React.createElement("div", { style: { fontSize: "13px", color: C.textMuted } },
        `${entries.length} ${currentType.singular.toLowerCase()}${entries.length === 1 ? "" : "s"} on file`
      ),
      React.createElement("div", { style: { display: "flex", gap: "8px" } },
        canImport && React.createElement("label", {
          style: {
            padding: "8px 14px", borderRadius: "8px",
            border: `1px solid ${C.warmGray}`, background: "#fff",
            color: C.textDark, fontSize: "13px", fontWeight: 600,
            cursor: csvBusy ? "wait" : "pointer",
          }
        },
          csvBusy ? "Importing…" : "Import CSV",
          React.createElement("input", {
            type: "file", accept: ".csv", style: { display: "none" },
            disabled: csvBusy,
            onChange: (e) => { importCsv(e.target.files?.[0]); e.target.value = ""; },
          }),
        ),
        canWrite && React.createElement("button", {
          onClick: () => { setShowAdd(v => !v); setForm(EMPTY_FORM); },
          style: {
            padding: "8px 14px", borderRadius: "8px", border: "none",
            background: showAdd ? C.warmGray : C.gold,
            color: showAdd ? C.textDark : "#fff",
            fontSize: "13px", fontWeight: 600, cursor: "pointer",
          }
        }, showAdd ? "Cancel" : `+ Add ${currentType.singular}`),
      ),
    ),

    // CSV import summary card
    csvResult && React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "10px", padding: "14px 18px",
        border: `1px solid ${C.warmGray}`, marginBottom: "16px",
        fontSize: "13px", color: C.textDark,
      }
    },
      React.createElement("div", { style: { fontWeight: 600, marginBottom: "6px", color: C.navy } }, "CSV import result"),
      React.createElement("div", null,
        `${csvResult.inserted} new · ${csvResult.updated} updated · ${csvResult.skipped} skipped`),
      csvResult.errors && csvResult.errors.length > 0 && React.createElement("ul", {
        style: { marginTop: "8px", marginLeft: "20px", color: C.textMuted }
      },
        ...csvResult.errors.map((err, i) => React.createElement("li", { key: i }, err)),
      ),
    ),

    // Add form
    showAdd && canWrite && React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "10px", padding: "20px",
        border: `1px solid ${C.warmGray}`, marginBottom: "16px",
      }
    },
      _entryFormFields(form, setForm),
      React.createElement("div", { style: { display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "12px" } },
        React.createElement("button", {
          onClick: () => { setShowAdd(false); setForm(EMPTY_FORM); },
          style: {
            padding: "6px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textMuted, fontSize: "12px", cursor: "pointer",
          }
        }, "Cancel"),
        React.createElement("button", {
          onClick: createEntry,
          disabled: saving || !form.name.trim(),
          style: {
            padding: "6px 16px", borderRadius: "6px", border: "none",
            background: (saving || !form.name.trim()) ? C.warmGray : C.gold,
            color: "#fff", fontSize: "12px", fontWeight: 600,
            cursor: (saving || !form.name.trim()) ? "default" : "pointer",
          }
        }, saving ? "Saving…" : "Save"),
      ),
    ),

    // Entry list / table
    loading
      ? React.createElement("div", { style: { padding: "40px", color: C.textMuted, fontSize: "13px" } },
          "Loading…")
      : entries.length === 0
        ? React.createElement("div", {
            style: {
              padding: "40px", textAlign: "center", color: C.textMuted,
              background: "#fff", borderRadius: "10px",
              border: `1px solid ${C.warmGray}`, fontSize: "13px",
            }
          }, `No ${currentType.label.toLowerCase()} yet. Add one manually or import a CSV.`)
        : React.createElement("div", {
            style: { background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
          },
            React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
              React.createElement("thead", null,
                React.createElement("tr", { style: { background: C.warmWhite } },
                  _th("Name"), _th("Category"), _th("Phone"), _th("Email"), _th(""),
                ),
              ),
              React.createElement("tbody", null,
                ...entries.flatMap(row => {
                  if (editingId === row.id) {
                    return [React.createElement("tr", { key: row.id + "-edit" },
                      React.createElement("td", { colSpan: 5, style: { padding: "16px", background: C.warmWhite } },
                        _entryFormFields(editForm, setEditForm),
                        React.createElement("div", { style: { display: "flex", gap: "6px", justifyContent: "flex-end", marginTop: "10px" } },
                          React.createElement("button", {
                            onClick: () => { setEditingId(null); setEditForm(EMPTY_FORM); },
                            style: {
                              padding: "5px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                              background: "#fff", color: C.textMuted, fontSize: "11px", cursor: "pointer",
                            }
                          }, "Cancel"),
                          React.createElement("button", {
                            onClick: () => saveEdit(row.id),
                            style: {
                              padding: "5px 14px", borderRadius: "6px", border: "none",
                              background: C.gold, color: "#fff", fontSize: "11px",
                              fontWeight: 600, cursor: "pointer",
                            }
                          }, "Save"),
                        ),
                      ),
                    )];
                  }
                  return [React.createElement("tr", {
                    key: row.id,
                    style: { borderTop: `1px solid ${C.warmGray}` }
                  },
                    _td(React.createElement("div", { style: { fontWeight: 600, color: C.navy } }, row.name)),
                    _td(row.category || "—"),
                    _td(row.phone || "—"),
                    _td(row.email || "—"),
                    _td(React.createElement("div", { style: { display: "flex", gap: "4px", justifyContent: "flex-end" } },
                      canWrite && React.createElement("button", {
                        onClick: () => {
                          setEditingId(row.id);
                          setEditForm({
                            name: row.name || "", category: row.category || "",
                            address: row.address || "", phone: row.phone || "",
                            fax: row.fax || "", email: row.email || "", notes: row.notes || "",
                          });
                        },
                        style: {
                          padding: "4px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                          background: "#fff", color: C.textDark, fontSize: "11px", cursor: "pointer",
                        }
                      }, "Edit"),
                      canDelete && React.createElement("button", {
                        onClick: () => removeEntry(row),
                        style: {
                          padding: "4px 10px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                          background: "#fff", color: C.error, fontSize: "11px", cursor: "pointer",
                        }
                      }, "Delete"),
                    )),
                  )];
                }),
              ),
            ),
          ),
  );
}

// ─── Small helpers ─────────────────────────────────────────

function _entryFormFields(form, setForm) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" } },
    _field("Name*", _input(form.name, set("name"), "Required")),
    _field("Category", _input(form.category, set("category"), "e.g. Imaging, Auto")),
    _field("Phone", _input(form.phone, set("phone"), "")),
    _field("Fax", _input(form.fax, set("fax"), "")),
    _field("Email", _input(form.email, set("email"), "")),
    _field("Address", _input(form.address, set("address"), "")),
    React.createElement("div", { style: { gridColumn: "1 / -1" } },
      _field("Notes", React.createElement("textarea", {
        value: form.notes, onChange: set("notes"), rows: 2,
        placeholder: "",
        style: {
          width: "100%", padding: "6px 8px", border: `1px solid ${C.warmGray}`,
          borderRadius: "6px", fontSize: "12px", boxSizing: "border-box",
          fontFamily: "inherit", resize: "vertical",
        }
      })),
    ),
  );
}

function _field(label, control) {
  return React.createElement("div", null,
    React.createElement("label", {
      style: { display: "block", fontSize: "11px", fontWeight: 600, color: C.navy, marginBottom: "3px" }
    }, label),
    control,
  );
}

function _input(value, onChange, placeholder) {
  return React.createElement("input", {
    type: "text", value, onChange, placeholder,
    style: {
      width: "100%", padding: "6px 8px", border: `1px solid ${C.warmGray}`,
      borderRadius: "6px", fontSize: "12px", boxSizing: "border-box",
    }
  });
}

function _th(text) {
  return React.createElement("th", {
    style: {
      textAlign: "left", padding: "10px 14px", fontSize: "11px",
      color: C.textMuted, textTransform: "uppercase",
      letterSpacing: "0.06em", fontWeight: 600,
    }
  }, text);
}

function _td(content) {
  return React.createElement("td", {
    style: { padding: "10px 14px", fontSize: "13px", color: C.textDark }
  }, content);
}
